/**
 * Market-overview data layer (ARCHITECTURE.md §7, §8).
 *
 * The `/market` route reads five display-only, session-only endpoints under
 * `/api/market` — headline indices, the SPDR sector snapshot, the CNN Fear &
 * Greed reading + its daily history, and merged market news. None of these feed
 * an order, a budget, or a P&L calc: the wire carries plain JS numbers (not the
 * branded `Decimal`), so this module never touches money helpers.
 *
 * Every read funnels through the typed `hc<AppType>` client and {@link unwrap},
 * so failures surface as `ApiError` and TanStack Query owns the server state.
 * Keys come from the shared `lib/queryKeys` registry. Cadences match how fast
 * each feed actually moves: indices tick fastest, Fear & Greed barely at all.
 */
import { queryOptions } from "@tanstack/react-query";
import type {
  MarketFgiHistoryResponse,
  MarketFgiResponse,
  MarketIndicesResponse,
  MarketNewsResponse,
  MarketSectorsResponse,
  MarketStocksResponse,
} from "@richrich/contracts";
import { api, unwrap } from "../../lib/api";
import { keys } from "../../lib/queryKeys";

/** Default Fear & Greed history window (days) shown under the gauge. */
export const FGI_HISTORY_DAYS = 90;

/** Empty index payload so the tile row can render a loading/empty frame. */
export function emptyIndices(): MarketIndicesResponse {
  return { asOf: 0, indices: [] };
}

/** Empty sector payload for the heatmap loading/empty frame. */
export function emptySectors(): MarketSectorsResponse {
  return { asOf: 0, sectors: [] };
}

/** Empty per-stock payload for the treemap loading/empty frame. */
export function emptyStocks(): MarketStocksResponse {
  return { asOf: 0, baselineDate: null, stocks: [] };
}

/** Empty Fear & Greed history for the trend sparkline. */
export function emptyFgiHistory(): MarketFgiHistoryResponse {
  return { points: [] };
}

/** Empty news payload for the headlines panel. */
export function emptyNews(): MarketNewsResponse {
  return { asOf: 0, items: [] };
}

/** `GET /api/market/indices` — headline indices + intraday sparklines. */
export const indicesQuery = queryOptions({
  queryKey: keys.market.indices(),
  queryFn: async (): Promise<MarketIndicesResponse> =>
    unwrap<MarketIndicesResponse>(await api.market.indices.$get()),
  staleTime: 30_000,
  refetchInterval: 60_000,
});

/** `GET /api/market/sectors` — the 11 SPDR sector ETFs (heatmap). */
export const sectorsQuery = queryOptions({
  queryKey: keys.market.sectors(),
  queryFn: async (): Promise<MarketSectorsResponse> =>
    unwrap<MarketSectorsResponse>(await api.market.sectors.$get()),
  staleTime: 120_000,
  refetchInterval: 300_000,
});

/**
 * `GET /api/market/stocks` — the per-stock treemap (종목 히트맵). Feature-gated
 * behind `trading` (Toss): callers pass `enabled: features.toss` so a locked
 * instance renders the gate CTA instead of hitting a guaranteed 403.
 */
export const stocksQuery = queryOptions({
  queryKey: keys.market.stocks(),
  queryFn: async (): Promise<MarketStocksResponse> =>
    unwrap<MarketStocksResponse>(await api.market.stocks.$get()),
  staleTime: 30_000,
  refetchInterval: 60_000,
});

/** `GET /api/market/fgi` — the current CNN Fear & Greed reading. */
export const fgiQuery = queryOptions({
  queryKey: keys.market.fgi(),
  queryFn: async (): Promise<MarketFgiResponse> =>
    unwrap<MarketFgiResponse>(await api.market.fgi.$get()),
  staleTime: 1_800_000, // 30 min — CNN updates a few times a day.
});

/** `GET /api/market/fgi/history?days=` — the daily Fear & Greed series. */
export function fgiHistoryQuery(days: number = FGI_HISTORY_DAYS) {
  return queryOptions({
    queryKey: keys.market.fgiHistory(days),
    queryFn: async (): Promise<MarketFgiHistoryResponse> =>
      unwrap<MarketFgiHistoryResponse>(
        await api.market.fgi.history.$get({ query: { days: String(days) } }),
      ),
    staleTime: 3_600_000, // 1 h.
  });
}

/** `GET /api/market/news` — merged CNBC + MarketWatch headlines. */
export const newsQuery = queryOptions({
  queryKey: keys.market.news(),
  queryFn: async (): Promise<MarketNewsResponse> =>
    unwrap<MarketNewsResponse>(await api.market.news.$get()),
  staleTime: 300_000,
  refetchInterval: 600_000,
});
